import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, FileText, GraduationCap, MessageSquare, Phone, Settings2, ShieldAlert, TrendingDown, Zap } from 'lucide-react';
import { motion } from 'framer-motion';
import { PointSystemConfigPanel } from './PointSystemConfigPanel';
import { MetricCategory, PointMetricDefinition, readPointMetricConfig, savePointMetricConfig } from '../data/pointSystemConfig';
import { getSourceObjectSchema } from '../data/querySchema';
const CATEGORY_ORDER: MetricCategory[] = ['Core Production', 'Speed & Quality Bonuses', 'Response Time', 'Dial Activity', 'Loan Partner Requirements', 'Quality Control & Penalties'];
const CATEGORY_DETAILS: Record<MetricCategory, {
  color: 'blue' | 'orange' | 'green' | 'purple' | 'teal' | 'red';
  icon: React.ReactNode;
  intro?: string;
}> = {
  'Core Production': {
    color: 'blue',
    icon: <FileText className="h-5 w-5 text-blue-600" data-id="element-620" data-mp="jz9ms7tqw6ok" />
  },
  'Speed & Quality Bonuses': {
    color: 'orange',
    icon: <Zap className="h-5 w-5 text-orange-500" data-id="element-621" data-mp="292ja2p251afq" />
  },
  'Response Time': {
    color: 'green',
    icon: <MessageSquare className="h-5 w-5 text-green-600" data-id="element-622" data-mp="243zsf0ukukt6" />,
    intro: 'Measured as a monthly average for current and past clients. Missed calls returned within 10 minutes count as answered.'
  },
  'Dial Activity': {
    color: 'purple',
    icon: <Phone className="h-5 w-5 text-purple-600" data-id="element-623" data-mp="2rs995ocxoidg" />
  },
  'Loan Partner Requirements': {
    color: 'teal',
    icon: <GraduationCap className="h-5 w-5 text-teal-600" data-id="element-624" data-mp="2mto4ncqnfcj7" />
  },
  'Quality Control & Penalties': {
    color: 'red',
    icon: <ShieldAlert className="h-5 w-5 text-red-600" data-id="element-625" data-mp="31pdwvsmfglwd" />
  }
};
export function PointSystem() {
  const [metrics, setMetrics] = useState<PointMetricDefinition[]>(() => readPointMetricConfig());
  const [configOpen, setConfigOpen] = useState(false);
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key) setMetrics(readPointMetricConfig());
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);
  const metricsByCategory = useMemo(() => {
    return CATEGORY_ORDER.map(category => ({
      category,
      metrics: metrics.filter(metric => metric.category === category && metric.enabled)
    }));
  }, [metrics]);
  const handleSave = (nextMetrics: PointMetricDefinition[]) => {
    setMetrics(nextMetrics);
    savePointMetricConfig(nextMetrics);
  };
  return <>
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="space-y-6" data-id="element-626" data-mp="2f0d2qf6my3iy">
        {configOpen ? <PointSystemConfigPanel metrics={metrics} onClose={() => setConfigOpen(false)} onSave={handleSave} data-id="element-627" data-mp="2os93jy03w7qv" /> : <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm" data-id="element-628" data-mp="2ypwvyha8p35v">
          <div className="flex items-center justify-between gap-4 bg-[#0a1e5e] px-6 py-5" data-id="element-629" data-mp="2ynj0w8fk7qyb">
            <div data-id="element-630" data-mp="n076j82pnwww">
              <div className="flex items-center gap-2" data-id="element-631" data-mp="i3qj6g3c5mkv">
                <h1 className="text-2xl font-extrabold tracking-tight text-white" data-id="element-632" data-mp="i1vzt67pq2iw">
                  Point System Reference
                </h1>
                <span className="rounded-full bg-white/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-blue-100" data-id="element-633" data-mp="85j648c63oo5">
                  Live rules
                </span>
              </div>
              <p className="mt-1 text-sm font-medium text-blue-200" data-id="element-634" data-mp="10u85o2xkrlxs">
                Complete guide to earning points, bonuses, and avoiding deductions.
              </p>
            </div>
            <button type="button" onClick={() => setConfigOpen(true)} className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-sm font-bold text-white transition-colors hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white" aria-label="Open point system admin configuration" data-id="element-635" data-mp="r459jtv5prv8">
              <Settings2 className="h-4 w-4" data-id="element-636" data-mp="19afci622ddiu" />
              <span className="hidden sm:inline" data-id="element-637" data-mp="19b799k7edhap">Admin settings</span>
            </button>
          </div>

          <div className="grid grid-cols-1 gap-8 p-6 lg:grid-cols-2" data-id="element-638" data-mp="ueg9vaxq45l3">
            {metricsByCategory.map(({
            category,
            metrics: categoryMetrics
          }) => {
            const details = CATEGORY_DETAILS[category];
            const penalties = category === 'Quality Control & Penalties';
            return <div key={category} className={penalties ? 'lg:col-span-2' : undefined} data-id="element-639" data-mp="1s285i0h4uyqk">
                  <Section title={category} icon={details.icon} color={details.color} data-id="element-640" data-mp="1i9slyyq6u23k">
                    {details.intro && <div className="mb-4 rounded-lg border border-green-100 bg-green-50 p-3 text-xs text-gray-600" data-id="element-641" data-mp="2arwvrga1hqzl">
                        {details.intro}
                      </div>}
                    {categoryMetrics.length === 0 ? <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-500" data-id="element-642" data-mp="91pmsdj7podx">
                        No active metrics in this group. Use Admin settings to
                        enable one.
                      </div> : penalties ? <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-id="element-643" data-mp="37jl8fm16i4te">
                        {categoryMetrics.map(metric => <PenaltyCard key={metric.id} metric={metric} data-id="element-644" data-mp="3ljw2sz1p61jb" />)}
                      </div> : <div className="divide-y divide-gray-100" data-id="element-645" data-mp="10slpwmfh0eo0">
                        {categoryMetrics.map(metric => <Rule key={metric.id} metric={metric} data-id="element-646" data-mp="wq8lq2gdgotc" />)}
                      </div>}
                  </Section>
                </div>;
          })}
          </div>
        </div>}
      </motion.div>
    </>;
}
function Section({
  title,
  icon,
  children,
  color
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  color: 'blue' | 'orange' | 'green' | 'purple' | 'teal' | 'red';
}) {
  const colorMap = {
    blue: 'border-blue-100',
    orange: 'border-orange-100',
    green: 'border-green-100',
    purple: 'border-purple-100',
    teal: 'border-teal-100',
    red: 'border-red-100'
  };
  return <section className={`rounded-xl border-2 p-5 ${colorMap[color]}`} data-id="element-647" data-mp="3fuwaeye6e701">
      <div className="mb-4 flex items-center gap-2" data-id="element-648" data-mp="3kmfhgy6pe960">
        {icon}
        <h2 className="text-lg font-extrabold text-gray-900" data-id="element-649" data-mp="3kos2xlbo71nt">{title}</h2>
      </div>
      {children}
    </section>;
}
function Rule({
  metric
}: {
  metric: PointMetricDefinition;
}) {
  const positive = metric.points > 0;
  const negative = metric.points < 0;
  const schema = getSourceObjectSchema(metric.sourceObject);
  const userField = schema.userFields.find(field => field.key === metric.sourceUserField)?.label ?? metric.sourceUserField;
  const timeframeField = schema.sourceFields.find(field => field.key === metric.timeframeField)?.label ?? metric.timeframeField;
  return <div className="flex items-start justify-between gap-4 py-3 first:pt-0 last:pb-0" data-id="element-650" data-mp="26pf6bdcwmgbq">
      <div className="min-w-0" data-id="element-651" data-mp="21q2o55wz9xfh">
        <div className="flex flex-wrap items-center gap-2" data-id="element-652" data-mp="1wxj69wr9cg4v">
          <p className="text-base font-bold leading-tight text-gray-800" data-id="element-653" data-mp="1wv6hp0qzv7iu">
            {metric.label}
          </p>
          <span className="rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] font-bold text-blue-700" data-id="element-654" data-mp="1rvttocodzueo">
            {schema.label}
          </span>
          {metric.customQueryEnabled && <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800" data-id="element-655" data-mp="1izkzpdj6hnzx">
              Custom override
            </span>}
          {metric.conditions.length > 0 && <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-bold text-gray-500" data-id="element-656" data-mp="36u7lk0qj5621">
              {metric.conditions.length} condition
              {metric.conditions.length === 1 ? '' : 's'}
            </span>}
        </div>
        <p className="mt-0.5 text-xs leading-snug text-gray-500" data-id="element-657" data-mp="2xryc7e6zziq8">
          {metric.criteria}
        </p>
        <p className="mt-1 text-[10px] font-semibold text-gray-400" data-id="element-658" data-mp="2j3alpmc0dz75">
          Attributed by: {userField}
          {timeframeField ? ` · Timeframe on: ${timeframeField}` : ''}
        </p>
      </div>
      <div className={`shrink-0 text-lg font-extrabold ${positive ? 'text-green-600' : negative ? 'text-red-600' : 'text-gray-400'}`} data-id="element-659" data-mp="2a5rwe5rc0ypg">
        {positive ? '+' : ''}
        {metric.points.toLocaleString()}
      </div>
    </div>;
}
function PenaltyCard({
  metric
}: {
  metric: PointMetricDefinition;
}) {
  const schema = getSourceObjectSchema(metric.sourceObject);
  const icon = metric.id === 'file-not-moving' ? <TrendingDown className="h-4 w-4 text-red-600" data-id="element-660" data-mp="1gjz8ixj8dz6w" /> : <AlertTriangle className="h-4 w-4 text-red-600" data-id="element-661" data-mp="1glscg52gm5b7" />;
  return <article className="rounded-lg border border-red-100 bg-red-50 p-4" data-id="element-662" data-mp="1vi9m8rv03bvn">
      <div className="mb-2 flex items-center gap-2" data-id="element-663" data-mp="1qhlzwjhpvj7r">
        {icon}
        <h3 className="text-sm font-bold text-red-900" data-id="element-664" data-mp="1qkhxg6pnk5vo">{metric.label}</h3>
      </div>
      <p className="mb-3 text-xs leading-snug text-red-800" data-id="element-665" data-mp="24g3lobo2aln1">
        {metric.criteria}
      </p>
      <div className="flex items-center justify-between gap-2" data-id="element-666" data-mp="1zijukb69medo">
        <span className="inline-block rounded bg-white px-2 py-1 text-sm font-extrabold text-red-600 shadow-sm" data-id="element-667" data-mp="2e6oe1hd56txi">
          {metric.points.toLocaleString()} PTS
        </span>
        <span className="text-[10px] font-bold text-red-700" data-id="element-668" data-mp="2n84o1o01ou17">
          {metric.customQueryEnabled ? 'Custom override active' : `${schema.label} · ${metric.conditions.length} condition${metric.conditions.length === 1 ? '' : 's'}`}
        </span>
      </div>
    </article>;
}